import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowStep} from "../workflow-step";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {WorkflowService} from "../workflow.service";
import {DeploymentService} from "../../deployment/deployment.service";
import {ProjectService} from "../../project/project.service";
import {ProjectModel, projectModelFactory} from "../../project/project.model";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {DeploymentGqlModel} from "../../deployment/deployment.gql-model";
import {toggleLoading} from "../../misc/loading.decorator";
import {DefaultNameIndexEditingGqlModel, NameIndexEditingItemGqlModel} from "../../graphql/common.gql-model";
import {get} from "lodash";

@Component({
  templateUrl: "./deployment-details.template.pug",
  providers: [
    DeploymentService,
    ProjectService,
  ],
})
export class DeploymentDetails extends WorkflowStep {
  deploymentId: string;
  siteId: string;
  project: ProjectModel = projectModelFactory();
  deploymentName: string;
  deployment: DeploymentGqlModel = new DeploymentGqlModel({});
  estimatedCompleteDate: string;
  estimatedInstallationDate: string;
  requestedInstallationDate: string;
  estimatedSubmissionDate: string;
  workOrderEndSignature: boolean = false;
  sourceName: string;
  error: Error;

  @toggleLoading()
  @handleErrors()
  async loadData(): Promise<void> {
    [this.deployment, this.project] = await Promise.all([
      this.deploymentService.getDeploymentById(this.workflowService.workflowConfig.targetId),
      this.projectService.getProjectById(this.workflowService.workflowConfig.projectId).toPromise(),
    ]);
  }

  ready() {
    this.loadData();
  }

  constructor(
    private deploymentService: DeploymentService,
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    private projectService: ProjectService,
  ) {
    super();
  }

  @handleErrors()
  async autoSave(): Promise<boolean> {
    await this.deploymentService.saveDeployment(this.deployment);

    await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);

    return true;
  }

  addQualityChecklistItem() {
    const newItem = new NameIndexEditingItemGqlModel({});
    this.deployment.qualityChecklist.insertForAdd(newItem);
    newItem.editing = true;
  }

  copyQualityChecklistFromProject() {
    this.deployment.qualityChecklist = new DefaultNameIndexEditingGqlModel(this.project.qualityChecklist);
    this.deployment.form.setControl("qualityChecklist", this.deployment.qualityChecklist.form);
    this.deployment.qualityChecklist.form.markAsDirty();
  }

  addInstallationInstructionsMAIItem() {
    const newItem = new NameIndexEditingItemGqlModel({});
    this.deployment.installationInstructionsMAI.insertForAdd(newItem);
    newItem.editing = true;
  }

  copyInstallationInstructionsMAIProject() {
    this.deployment.installationInstructionsMAI = new DefaultNameIndexEditingGqlModel(
      get(this.project, "installationInstructions.mai") || [],
    );

    this.deployment.form.setControl("installationInstructionsMAI", this.deployment.installationInstructionsMAI.form);
    this.deployment.installationInstructionsMAI.form.markAsDirty();
  }

  addInstallationInstructionsLeaseItem() {
    const newItem = new NameIndexEditingItemGqlModel({});
    this.deployment.installationInstructionsLease.insertForAdd(newItem);
    newItem.editing = true;
  }

  copyInstallationInstructionsLeaseProject() {
    this.deployment.installationInstructionsLease = new DefaultNameIndexEditingGqlModel(
      get(this.project, "installationInstructions.lease") || [],
    );

    this.deployment.form.setControl(
      "installationInstructionsLease",
      this.deployment.installationInstructionsLease.form,
    );

    this.deployment.installationInstructionsLease.form.markAsDirty();
  }

  public checkDirty(): boolean {
    return this.deployment.form.dirty;
  }
}
